import { useEffect, useRef } from 'react';

import toast from 'common/components/Toast';
import { SECOND_IN_MS } from 'common/constants';

type UseStaleFilterNotificationOptions = {
  filterName: string;
  persistedValue?: Set<string>;
  availableValues?: Set<string>;
  setPersistedValue?: (value?: Set<string>) => void;
};

function useStaleFilterNotification({
  filterName,
  persistedValue,
  availableValues,
  setPersistedValue,
}: UseStaleFilterNotificationOptions): void {
  const hasFiredRef = useRef(false);

  useEffect(() => {
    if (hasFiredRef.current) return;
    if (!persistedValue || !availableValues) return;

    const stale = [...persistedValue].filter(value => !availableValues.has(value));
    if (stale.length === 0) return;

    hasFiredRef.current = true;

    toast(
      {
        severity: 'info',
        title: 'Filter not fully applied',
        content: `The following ${filterName} are no longer available and were excluded: ${stale.join(', ')}.`,
        ...(setPersistedValue && {
          closeTextButton: 'Remove stale filters',
          onClose: () => {
            const validOnly = new Set(
              [...persistedValue].filter(value => availableValues.has(value)),
            );
            setPersistedValue(validOnly.size === 0 ? undefined : validOnly);
          },
        }),
      },
      { autoClose: 5 * SECOND_IN_MS },
    );
  }, [availableValues, filterName, persistedValue, setPersistedValue]);
}

export default useStaleFilterNotification;
